/**
 * UserTable Component
 *
 * Reusable table component for displaying users with sorting and actions.
 */

import React from 'react';
import { Edit2, Trash2, Eye, MoreVertical, CreditCard, Shield } from 'lucide-react';
import { RoleBadge } from './RoleBadge';
import { SubscriptionBadge } from './SubscriptionBadge';
import { StatusBadge } from './StatusBadge';
import type { AdminUser } from '../../services/adminApi';

interface UserTableProps {
  users: AdminUser[];
  loading?: boolean;
  onEdit?: (user: AdminUser) => void;
  onDelete?: (user: AdminUser) => void;
  onViewDetails?: (user: AdminUser) => void;
  onViewPermissions?: (user: AdminUser) => void;
  onChangeSubscription?: (user: AdminUser) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  emptyMessage?: string;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  loading = false,
  onEdit,
  onDelete,
  onViewDetails,
  onViewPermissions,
  onChangeSubscription,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage = 'No users found',
}) => {
  const [openMenu, setOpenMenu] = React.useState<number | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    if (openMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [openMenu]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const SortHeader: React.FC<{ column: string; label: string }> = ({ column, label }) => {
    const isActive = sortBy === column;
    return (
      <button
        onClick={() => onSort?.(column)}
        className={`
          flex items-center gap-1 text-xs font-medium uppercase tracking-wider
          ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}
          transition-colors
        `}
      >
        {label}
        {isActive && (
          <svg
            className={`w-3 h-3 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 rounded-t-lg mb-1" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-50 mb-1 rounded" />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-lg">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left">
              {onSort ? <SortHeader column="id" label="ID" /> : <span className="text-xs font-medium text-gray-500 uppercase">ID</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left">
              {onSort ? <SortHeader column="email" label="Email" /> : <span className="text-xs font-medium text-gray-500 uppercase">Email</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden md:table-cell">
              {onSort ? <SortHeader column="companyName" label="Company" /> : <span className="text-xs font-medium text-gray-500 uppercase">Company</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left">
              <span className="text-xs font-medium text-gray-500 uppercase">Role</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden lg:table-cell">
              <span className="text-xs font-medium text-gray-500 uppercase">Tier</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden sm:table-cell">
              <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden xl:table-cell">
              {onSort ? <SortHeader column="lastLogin" label="Last Login" /> : <span className="text-xs font-medium text-gray-500 uppercase">Last Login</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden lg:table-cell">
              {onSort ? <SortHeader column="createdAt" label="Created" /> : <span className="text-xs font-medium text-gray-500 uppercase">Created</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              <span className="text-xs font-medium text-gray-500 uppercase">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                #{user.id}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    {(user.firstName || user.lastName) && (
                      <p className="text-xs text-gray-500">
                        {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">
                {user.companyName || '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <RoleBadge
                    role={user.rbacRole || user.role}
                    displayName={user.rbacRoleDisplay}
                    size="sm"
                  />
                  {user.permissionCount !== undefined && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {user.permissionCount} perms
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <SubscriptionBadge tier={user.subscriptionTier} size="sm" />
              </td>
              <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                <StatusBadge status={user.status} size="sm" />
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                {formatDateTime(user.lastLogin)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                {formatDate(user.createdAt)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-1">
                  {/* Quick action buttons - visible on larger screens */}
                  <div className="hidden sm:flex items-center gap-1">
                    {onViewDetails && (
                      <button
                        onClick={() => onViewDetails(user)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {onViewPermissions && (
                      <button
                        onClick={() => onViewPermissions(user)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View permissions"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(user)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit user"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(user)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* More menu - always visible */}
                  <div className="relative" ref={openMenu === user.id ? menuRef : null}>
                    <button
                      onClick={() => setOpenMenu(openMenu === user.id ? null : user.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {openMenu === user.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        {onViewDetails && (
                          <button
                            onClick={() => {
                              onViewDetails(user);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View Details
                          </button>
                        )}
                        {onViewPermissions && (
                          <button
                            onClick={() => {
                              onViewPermissions(user);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            View Permissions
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => {
                              onEdit(user);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit User
                          </button>
                        )}
                        {onChangeSubscription && (
                          <button
                            onClick={() => {
                              onChangeSubscription(user);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <CreditCard className="w-4 h-4" />
                            Change Subscription
                          </button>
                        )}
                        {onDelete && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => {
                                onDelete(user);
                                setOpenMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete User
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
